import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { mockCampsites } from '@/data/mockCampsites';
import type { Campsite } from '@/types/campsite';
import CampsiteDetail from './CampsiteDetail';

// Set Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoiZGFmaWRhZmkyNSIsImEiOiJjbTdsczg0Y3QwYnEwMmpwemIxZ2h4dHY0In0.dcTST_LLYTlp-t_oUkB0dw';

const MapMode: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [selectedCampsite, setSelectedCampsite] = useState<Campsite | null>(null);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: [114.25, 22.45], // Hong Kong coordinates
      zoom: 10
    });

    map.current.on('load', () => {
      if (!map.current) return;

      // Add campsite data as GeoJSON source
      map.current.addSource('campsites', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: mockCampsites.map(campsite => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [campsite.coordinates.lng, campsite.coordinates.lat]
            },
            properties: {
              id: campsite.id,
              name: campsite.name,
              location: campsite.location,
              occupancy: `${campsite.occupancy.current}/${campsite.occupancy.total}`,
              rating: campsite.rating
            }
          }))
        },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50
      });

      // Cluster circles
      map.current.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'campsites',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#3B82F6',
          'circle-radius': ['step', ['get', 'point_count'], 20, 10, 25, 30, 30],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Cluster count
      map.current.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'campsites',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 14
        },
        paint: { 'text-color': '#ffffff' }
      });

      // Individual campsite markers
      map.current.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'campsites',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#3B82F6',
          'circle-radius': 18,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Click on cluster to zoom
      map.current.on('click', 'clusters', (e) => {
        if (!map.current) return;
        const features = map.current.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        if (!features || features.length === 0 || !features[0]) return;

        const clusterId = features[0].properties?.cluster_id;
        if (!clusterId) return;

        const source = map.current.getSource('campsites') as mapboxgl.GeoJSONSource;
        const geometry = features[0].geometry as any;
        if (!geometry?.coordinates) return;

        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || !map.current || zoom === null) return;
          map.current.easeTo({
            center: geometry.coordinates,
            zoom: zoom
          });
        });
      });

      // Click on individual campsite
      map.current.on('click', 'unclustered-point', (e) => {
        if (!e.features || e.features.length === 0 || !map.current) return;

        const feature = e.features[0];
        if (!feature) return;

        const geometry = feature.geometry as any;
        if (!geometry?.coordinates) return;

        const coordinates = geometry.coordinates.slice();
        const campsiteId = feature.properties?.id;

        // Find the full campsite data and show detail panel
        const campsite = mockCampsites.find(c => c.id === campsiteId);
        if (!campsite) return;

        setSelectedCampsite(campsite);
      });

      // Cursor pointer on hover
      ['clusters', 'unclustered-point'].forEach(layer => {
        map.current!.on('mouseenter', layer, () => {
          if (map.current) map.current.getCanvas().style.cursor = 'pointer';
        });
        map.current!.on('mouseleave', layer, () => {
          if (map.current) map.current.getCanvas().style.cursor = '';
        });
      });
    });

    // Clean up on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  return (
    <div className="flex-1 bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="flex flex-col">
        {/* Map Container */}
        <div className="h-[700px]">
          <div ref={mapContainer} className="w-full h-full" />
        </div>

        {/* Campsite Detail Panel at Bottom */}
        {selectedCampsite && (
          <div className="border-t border-gray-200">
            <CampsiteDetail
              campsite={selectedCampsite}
              onClose={() => setSelectedCampsite(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MapMode;
